import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../../state/auth-store';
import { Badge, Button, Card, Input } from '@workspace/ui';
import { User, Mail, Shield, Building2, Key } from 'lucide-react';

export function ProfilePage() {
  const { t } = useTranslation("layout");
  const { user, tenant } = useAuthStore();
  const [isEditing, setIsEditing] = useState(false);

  if (!user) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <p className="text-content-secondary">{t("profile.loading")}</p>
      </div>
    );
  }

  const getRoleLabel = (role: string) =>
    t(`roles.${role}`, { defaultValue: role });

  const getRoleBadgeVariant = (role: string) => {
    switch (role) {
      case 'ADMIN':
        return 'bg-state-dangerSoft text-state-danger';
      case 'MANAGER':
        return 'bg-state-warningSoft text-state-warning';
      case 'EMPLOYEE':
        return 'bg-primary/10 text-content-accent';
      default:
        return 'bg-surface-secondary text-content-secondary';
    }
  };

  return (
    <div className="min-h-screen bg-surface-page text-content-primary">
      <div className="max-w-4xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-content-primary">
            {t("profile.title")}
          </h1>
          <p className="mt-2 text-content-secondary">
            {t("profile.subtitle")}
          </p>
        </div>

        <Card paddingMode="bleed" className="p-6 mb-6">
          <div className="flex items-start justify-between mb-6">
            <div className="flex items-center gap-4">
              <div className="flex h-20 w-20 items-center justify-center rounded-full bg-primary/10">
                <span className="text-2xl font-bold text-content-accent">
                  {user.name.charAt(0).toUpperCase()}
                </span>
              </div>
              <div>
                <h2 className="text-2xl font-bold text-content-primary">{user.name}</h2>
                <p className="text-content-secondary">{user.email}</p>
                <div className="mt-2">
                  <Badge className={getRoleBadgeVariant(user.role)}>
                    {getRoleLabel(user.role)}
                  </Badge>
                </div>
              </div>
            </div>
            <Button variant="secondary" onClick={() => setIsEditing(!isEditing)}>
              {isEditing ? t("profile.actions.cancel") : t("profile.actions.edit")}
            </Button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="mb-2 flex items-center gap-2 text-sm font-medium text-content-secondary">
                <User className="h-4 w-4" />
                {t("profile.fields.fullName")}
              </label>
              <Input 
                value={user.name} 
                disabled={!isEditing}
                className={!isEditing ? 'bg-surface-secondary' : ''}
              />
            </div>

            <div>
              <label className="mb-2 flex items-center gap-2 text-sm font-medium text-content-secondary">
                <Mail className="h-4 w-4" />
                {t("profile.fields.email")}
              </label>
              <Input 
                value={user.email} 
                disabled
                className="bg-surface-secondary"
              />
              <p className="mt-1 text-xs text-content-muted">
                {t("profile.fields.emailLocked")}
              </p>
            </div>

            <div>
              <label className="mb-2 flex items-center gap-2 text-sm font-medium text-content-secondary">
                <Shield className="h-4 w-4" />
                {t("profile.fields.role")}
              </label>
              <Input 
                value={getRoleLabel(user.role)} 
                disabled
                className="bg-surface-secondary"
              />
            </div>

            <div>
              <label className="mb-2 flex items-center gap-2 text-sm font-medium text-content-secondary">
                <Building2 className="h-4 w-4" />
                {t("profile.fields.organization")}
              </label>
              <Input 
                value={tenant?.name || t("profile.notAvailable")}
                disabled
                className="bg-surface-secondary"
              />
            </div>
          </div>

          {isEditing && (
            <div className="mt-6 flex justify-end gap-3 border-t border-border-subtle pt-6">
              <Button variant="secondary" onClick={() => setIsEditing(false)}>
                {t("profile.actions.cancel")}
              </Button>
              <Button>
                {t("profile.actions.save")}
              </Button>
            </div>
          )}
        </Card>

        <Card paddingMode="bleed" className="p-6">
          <h3 className="mb-4 flex items-center gap-2 text-lg font-semibold text-content-primary">
            <Key className="h-5 w-5" />
            {t("profile.security.title")}
          </h3>
          
          <div className="space-y-4">
            <div className="flex items-center justify-between border-b border-border-subtle py-3">
              <div>
                <p className="font-medium text-content-primary">
                  {t("profile.security.password.title")}
                </p>
                <p className="text-sm text-content-muted">
                  {t("profile.security.password.lastUpdated")}
                </p>
              </div>
              <Button variant="secondary" size="sm">
                {t("profile.security.password.change")}
              </Button>
            </div>

            <div className="flex items-center justify-between border-b border-border-subtle py-3">
              <div>
                <p className="font-medium text-content-primary">
                  {t("profile.security.twoFactor.title")}
                </p>
                <p className="text-sm text-content-muted">
                  {t("profile.security.twoFactor.description")}
                </p>
              </div>
              <Badge className="bg-surface-secondary text-content-secondary">
                {t("common.comingSoon")}
              </Badge>
            </div>

            <div className="flex items-center justify-between py-3">
              <div>
                <p className="font-medium text-content-primary">
                  {t("profile.security.sessions.title")}
                </p>
                <p className="text-sm text-content-muted">
                  {t("profile.security.sessions.description")}
                </p>
              </div>
              <Button variant="ghost" size="sm">
                {t("profile.security.sessions.view")}
              </Button>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
}
